import { useState, useEffect } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Users, UserPlus, Mail, MoreHorizontal, Trash2, Clock, Info } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { teamService, TeamMember, TeamInvite, Team, Profile } from '@/services/teamService';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocalDate, formatLocalDateTime } from '@/lib/date';
import { TeamRole } from '@/types/roles';

const TeamManagement = () => {
  const { canInviteMembers, canManageTeam, isViewer } = useRoleAccess();
  const { isLoading: authLoading, user } = useAuth();
  const isAdminLike = canManageTeam || user?.teamRole === 'admin' || user?.teamRole === 'super_admin';
  
  // Debug logging to see what's happening with user data
  useEffect(() => {
    console.log('TeamManagement - Auth state:', {
      authLoading,
      user,
      userRole: user?.teamRole,
      canManageTeam,
      canInviteMembers
    });
  }, [authLoading, user, canManageTeam, canInviteMembers]);
  
  const [team, setTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamInvites, setTeamInvites] = useState<TeamInvite[]>([]);
  const [userRole, setUserRole] = useState<TeamRole>('viewer');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'viewer' as TeamRole
  });
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'role' | 'remove';
    memberId: string;
    memberName: string;
    newRole?: TeamRole;
  } | null>(null);

  // Load team data on component mount
  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [teamData, membersData, invitesData, profileData] = await Promise.all([
        teamService.getTeam(),
        teamService.getTeamMembers(),
        teamService.getTeamInvites(),
        teamService.getUserProfile()
      ]);
      
      setTeam(teamData);
      setTeamMembers(membersData);
      setTeamInvites(invitesData);
      setUserRole(profileData.teamRole);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load team data';
      setError(errorMessage);
      toast.error('Error loading team data', {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast.error('Email is required');
      return;
    }

    try {
      const newMember = await teamService.inviteTeamMember({
        email: inviteForm.email,
        role: inviteForm.role
      });

      // If user already exists, add to members. Otherwise, refresh invites
      if (newMember.userId !== '00000000-0000-0000-0000-000000000000') {
        setTeamMembers([newMember, ...teamMembers]);
      } else {
        // Refresh invites to get the new one
        const updatedInvites = await teamService.getTeamInvites();
        setTeamInvites(updatedInvites);
      }

      setInviteForm({ email: '', role: 'viewer' });
      setIsInviteDialogOpen(false);
      
      toast.success('Invitation sent', {
        description: `Invite sent to ${inviteForm.email}`
      });

      // Refresh team data to get updated counts
      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invitation';
      toast.error('Error sending invitation', {
        description: errorMessage
      });
    }
  };

  const handleRoleChange = async (memberId: string, newRole: TeamRole) => {
    // Prevent changing the last admin to viewer
    if (wouldLeaveNoAdmins(memberId, newRole)) {
      toast.error('Cannot change role', {
        description: 'Cannot change the last admin to viewer. At least one admin must remain in the team.'
      });
      return;
    }

    const member = teamMembers.find(m => m.id === memberId);
    if (!member) return;

    setConfirmAction({
      type: 'role',
      memberId,
      memberName: member.userName,
      newRole
    });
    setIsConfirmDialogOpen(true);
  };

  const handleRemoveMember = async (memberId: string) => {
    // Prevent removing the last admin
    if (isLastAdmin(memberId)) {
      toast.error('Cannot remove member', {
        description: 'Cannot remove the last admin from the team. At least one admin must remain.'
      });
      return;
    }

    const member = teamMembers.find(m => m.id === memberId);
    if (!member) return;

    setConfirmAction({
      type: 'remove',
      memberId,
      memberName: member.userName
    });
    setIsConfirmDialogOpen(true);
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      if (confirmAction.type === 'role' && confirmAction.newRole) {
        const updatedMember = await teamService.updateTeamMember(confirmAction.memberId, { role: confirmAction.newRole });
        setTeamMembers(teamMembers.map(member => 
          member.id === confirmAction.memberId ? updatedMember : member
        ));
        toast.success('Role updated successfully');
      } else if (confirmAction.type === 'remove') {
        await teamService.removeTeamMember(confirmAction.memberId);
        setTeamMembers(teamMembers.filter(member => member.id !== confirmAction.memberId));
        toast.success('Team member removed');

        // Refresh team data to get updated counts
        const updatedTeam = await teamService.getTeam();
        setTeam(updatedTeam);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to perform action';
      toast.error('Error performing action', {
        description: errorMessage
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await teamService.cancelInvite(inviteId);
      setTeamInvites(teamInvites.filter(invite => invite.id !== inviteId));
      toast.success('Invitation cancelled');

      // Refresh team data to get updated counts
      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel invitation';
      toast.error('Error cancelling invitation', {
        description: errorMessage
      });
    }
  };

  const getRoleBadge = (role: string, memberId?: string) => {
    const isLast = memberId ? isLastAdmin(memberId) : false;
    
    if (role === 'admin') {
      return (
        <div className="flex items-center gap-1">
          <Badge>Admin</Badge>
          {isLast && (
            <span className="text-xs text-muted-foreground">(Last)</span>
          )}
        </div>
      );
    }
    return <Badge variant="secondary">Viewer</Badge>;
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? 
      <Badge className="bg-green-100 text-green-800">Active</Badge> :
      <Badge variant="outline">Pending</Badge>;
  };

  const formatLastActive = (lastActive: string | undefined) => {
    if (!lastActive) return 'Never';
    return formatLocalDateTime(lastActive);
  };

  const formatJoinedDate = (joinedAt: string | undefined) => {
    if (!joinedAt) return 'Pending';
    return formatLocalDate(joinedAt);
  };

  const formatInviteDate = (invitedAt: string) => {
    return formatLocalDate(invitedAt);
  };

  const formatExpiryDate = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'Expired';
    if (daysUntilExpiry === 0) return 'Today';
    if (daysUntilExpiry === 1) return '1 day';
    return `${daysUntilExpiry} days`;
  };

  // Helper function to check if a member is the last admin
  const isLastAdmin = (memberId: string) => {
    const adminMembers = teamMembers.filter(member => member.role === 'admin');
    const targetMember = teamMembers.find(member => member.id === memberId);
    return adminMembers.length === 1 && targetMember?.role === 'admin';
  };

  // Helper function to check if changing role would leave no admins
  const wouldLeaveNoAdmins = (memberId: string, newRole: TeamRole) => {
    if (newRole === 'admin') return false; // Adding an admin is always safe
    const adminMembers = teamMembers.filter(member => member.role === 'admin');
    const targetMember = teamMembers.find(member => member.id === memberId);
    return adminMembers.length === 1 && targetMember?.role === 'admin';
  };

  // Show loading state - wait for both auth and team data to load
  // Also ensure we have user data before rendering (in case auth is loaded but user is still null)
  if (authLoading || isLoading || !user) {
    return (
      <PageLayout 
        title="Team Management" 
        description="Manage team members and their access permissions"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading team data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (error && !team) {
    return (
      <PageLayout 
        title="Team Management" 
        description="Manage team members and their access permissions"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">Error: {error}</p>
            <Button onClick={loadTeamData} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Using canManageTeam from useRoleAccess hook instead of local variable

      return (
      <PageLayout 
        title="Team Management" 
        description="Manage team members and their access permissions"
      >
        {isViewer && (
          <div className="mb-6 p-4 bg-blue-500/10 rounded-lg flex gap-3 items-start border border-blue-500/30">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Viewer Access</p>
              <p className="text-xs text-muted-foreground mt-1">You can view team members but cannot invite new members or modify team settings.</p>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
        {/* Team Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <Users className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-2xl font-bold">{team?.memberCount || 0}</div>
                  <div className="text-sm text-muted-foreground">Total Members</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <UserPlus className="h-8 w-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold">{team?.activeMemberCount || 0}</div>
                  <div className="text-sm text-muted-foreground">Active</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2">
                <Mail className="h-8 w-8 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold">{team?.pendingMemberCount || 0}</div>
                  <div className="text-sm text-muted-foreground">Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Warning for single admin */}
        {teamMembers.filter(member => member.role === 'admin').length === 1 && (
          <div className="p-4 bg-yellow-500/10 rounded-lg flex gap-3 items-start border border-yellow-500/30">
            <Info className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Single Admin Team</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your team has only one admin. This admin cannot be changed to viewer or removed to ensure team management capabilities are maintained.
              </p>
            </div>
          </div>
        )}

        {/* Add Member Button */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Team Members</h2>
          {isAdminLike && (
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus size={16} className="mr-2" />
                  Invite Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join your team
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite_email">Email</Label>
                    <Input
                      id="invite_email"
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteForm.role} onValueChange={(value: TeamRole) => setInviteForm({...inviteForm, role: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleInvite} className="w-full">
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={isConfirmDialogOpen && confirmAction !== null} onOpenChange={(open) => {
          setIsConfirmDialogOpen(open);
          if (!open) {
            setConfirmAction(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmAction?.type === 'role' ? 'Change Member Role' : 'Remove Team Member'}
              </DialogTitle>
              <DialogDescription>
                {confirmAction?.type === 'role' 
                  ? `Are you sure you want to change ${confirmAction?.memberName}'s role to ${confirmAction?.newRole}?`
                  : `Are you sure you want to remove ${confirmAction?.memberName} from the team? This action cannot be undone.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setIsConfirmDialogOpen(false);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={executeConfirmAction}
                variant={confirmAction?.type === 'remove' ? 'destructive' : 'default'}
              >
                {confirmAction?.type === 'role' ? 'Change Role' : 'Remove Member'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Team Members Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Joined</TableHead>
                {isAdminLike && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{member.userName}</div>
                      <div className="text-sm text-muted-foreground">{member.userEmail}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getRoleBadge(member.role, member.id)}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(member.status)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatLastActive(member.lastActive)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatJoinedDate(member.joinedAt)}
                  </TableCell>
                  {isAdminLike && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleRoleChange(member.id, member.role === 'admin' ? 'viewer' : 'admin')}
                            disabled={wouldLeaveNoAdmins(member.id, member.role === 'admin' ? 'viewer' : 'admin')}
                            className={wouldLeaveNoAdmins(member.id, member.role === 'admin' ? 'viewer' : 'admin') ? 'opacity-50 cursor-not-allowed' : ''}
                          >
                            Change to {member.role === 'admin' ? 'Viewer' : 'Admin'}
                            {wouldLeaveNoAdmins(member.id, member.role === 'admin' ? 'viewer' : 'admin') && (
                              <span className="ml-2 text-xs text-muted-foreground">(Last admin)</span>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleRemoveMember(member.id)}
                            disabled={isLastAdmin(member.id)}
                            className={`text-destructive ${isLastAdmin(member.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Trash2 size={14} className="mr-2" />
                            Remove Member
                            {isLastAdmin(member.id) && (
                              <span className="ml-2 text-xs text-muted-foreground">(Last admin)</span>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Pending Invitations */}
        {teamInvites.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <h2 className="text-xl font-bold">Pending Invitations</h2>
              <Badge variant="outline">{teamInvites.length}</Badge>
            </div>
            
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Expires</TableHead>
                    {isAdminLike && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamInvites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <div className="font-medium">{invite.email}</div>
                      </TableCell>
                      <TableCell>
                        {getRoleBadge(invite.role)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatInviteDate(invite.invitedAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className={formatExpiryDate(invite.expiresAt) === 'Expired' ? 'text-red-500' : ''}>
                          {formatExpiryDate(invite.expiresAt)}
                        </span>
                      </TableCell>
                      {isAdminLike && (
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleCancelInvite(invite.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 size={14} className="mr-2" />
                            Cancel
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default TeamManagement;
